export interface PigProps {
    color: string;
    variant: 'default' | 'wild' | 'boss';
    expression: 'neutral' | 'happy' | 'angry' | 'sleepy';
    markings: 'none' | 'spots' | 'blush' | 'stripes';
    hasTusks: boolean;
    accessory?: {
        type: 'none' | 'bow' | 'crown' | 'flower';
        color: string;
    };
}

export const PIG_PRESETS: Record<string, Partial<PigProps>> = {
  'Ribbon Pig': { color: '#FFC0CB', accessory: { type: 'bow', color: '#ff69b4' }, expression: 'happy' },
  'Spotted Pig': { color: '#FFB6C1', markings: 'spots' },
  'Sleepy Pig': { color: '#FFA07A', expression: 'sleepy', markings: 'blush' },
  'Angry Pig': { color: '#DB7093', expression: 'angry', hasTusks: true },
  'King Pig': { color: '#FF69B4', accessory: { type: 'crown', color: '#ffd700' }, variant: 'boss', hasTusks: true },
  'Party Pig': { color: '#FFB6C1', accessory: { type: 'flower', color: '#ff69b4' }, expression: 'happy' },
};

const PIG_COLORS = [
  '#FFC0CB', // Pink
  '#FFB6C1', // Light Pink
  '#FFA07A', // Light Salmon
  '#DB7093', // Pale Violet Red
  '#FF69B4', // Hot Pink
  '#D2691E', // Chocolate (Wild)
  '#8B4513'  // Saddle Brown (Wild)
];

const EXPRESSIONS = ['neutral', 'happy', 'angry', 'sleepy'];

const MARKINGS = ['none', 'spots', 'blush', 'stripes'];

/**
 * Generate a random pig configuration
 */
export const generateRandomPig = (_id: string): PigProps => {
  const isWild = Math.random() > 0.7;
  const isBoss = Math.random() > 0.95;
    
  return {
    color: isWild ? PIG_COLORS[Math.floor(Math.random() * 2) + 5] : PIG_COLORS[Math.floor(Math.random() * 5)],
    variant: isBoss ? 'boss' : (isWild ? 'wild' : 'default'),
    expression: EXPRESSIONS[Math.floor(Math.random() * EXPRESSIONS.length)] as any,
    markings: MARKINGS[Math.floor(Math.random() * MARKINGS.length)] as any,
    hasTusks: isWild || isBoss || Math.random() > 0.8,
    accessory: {
      type: Math.random() > 0.8 ? ['bow', 'crown', 'flower'][Math.floor(Math.random() * 3)] as any : 'none',
      color: PIG_COLORS[Math.floor(Math.random() * PIG_COLORS.length)]
    }
  };
};

/**
 * Generate SVG string for a pig based on its props
 */
export const generatePigSVG = (props: PigProps, size: number = 64): string => {
  const { color, variant, expression, markings, hasTusks, accessory } = props;
    
  // Scale for boss
  const scale = variant === 'boss' ? 1.5 : 1.0;
  const transform = `scale(${scale})`;
    
  // Expression specific paths
  const getExpression = (expr: string) => {
    switch (expr) {
    case 'happy': return { 
      eyes: '<path d="M40,40 Q45,35 50,40" stroke="#333" fill="none" stroke-width="2"/><path d="M70,40 Q75,35 80,40" stroke="#333" fill="none" stroke-width="2"/>',
      mouth: '<path d="M50,70 Q60,80 70,70" stroke="#333" fill="none" stroke-width="2"/>'
    };
    case 'angry': return {
      eyes: '<g><path d="M35,35 L50,45" stroke="#333" stroke-width="2"/><path d="M85,35 L70,45" stroke="#333" stroke-width="2"/><circle cx="45" cy="45" r="3" fill="#333"/><circle cx="75" cy="45" r="3" fill="#333"/></g>',
      mouth: '<path d="M55,75 L65,75" stroke="#333" stroke-width="2"/>'
    };
    case 'sleepy': return {
      eyes: '<path d="M40,45 L50,45" stroke="#333" stroke-width="2"/><path d="M70,45 L80,45" stroke="#333" stroke-width="2"/>',
      mouth: '<circle cx="60" cy="72" r="3" fill="#333" opacity="0.6"/>'
    };
    default: return {
      eyes: '<circle cx="45" cy="45" r="4" fill="#333"/><circle cx="75" cy="45" r="4" fill="#333"/><circle cx="46" cy="43" r="1.5" fill="white"/><circle cx="76" cy="43" r="1.5" fill="white"/>',
      mouth: '<path d="M55,72 L65,72" stroke="#333" stroke-width="2"/>'
    };
    }
  };

  const expr = getExpression(expression);
    
  // Markings
  const renderMarkings = (type: string, _baseColor: string) => {
    switch (type) {
    case 'spots': return `
                <circle cx="30" cy="40" r="5" fill="rgba(0,0,0,0.1)"/>
                <circle cx="90" cy="60" r="8" fill="rgba(0,0,0,0.1)"/>
                <circle cx="50" cy="30" r="4" fill="rgba(0,0,0,0.1)"/>`;
    case 'blush': return `
                <circle cx="35" cy="55" r="6" fill="#ffb6c1" opacity="0.5"/>
                <circle cx="85" cy="55" r="6" fill="#ffb6c1" opacity="0.5"/>`;
    case 'stripes': return `
                <path d="M40,25 Q50,30 60,25" stroke="rgba(0,0,0,0.1)" stroke-width="4" fill="none"/>
                <path d="M40,35 Q50,40 60,35" stroke="rgba(0,0,0,0.1)" stroke-width="4" fill="none"/>`;
    default: return '';
    }
  };

  // Tusks
  const tusks = hasTusks ? `
        <path d="M45,65 Q40,75 35,70" fill="white" stroke="#ccc" stroke-width="1"/>
        <path d="M75,65 Q80,75 85,70" fill="white" stroke="#ccc" stroke-width="1"/>` : '';

  // Accessory
  const renderAccessory = (acc: any, _angle: number) => {
    if (!acc || acc.type === 'none') return '';
    switch (acc.type) {
    case 'bow': return `
                <g transform="translate(90, 25) rotate(20)">
                    <path d="M-5,-5 L5,5 M5,-5 L-5,5" stroke="${acc.color}" stroke-width="8"/>
                    <circle r="3" fill="#fff"/>
                </g>`;
    case 'crown': return `
                <path d="M45,15 L50,5 L60,15 L70,5 L75,15 L45,15" fill="#ffd700" stroke="#b8860b"/>`;
    case 'flower': return `
                <g transform="translate(35, 20)">
                    <circle cx="0" cy="0" r="5" fill="${acc.color}"/>
                    <circle cx="0" cy="0" r="2" fill="#fff"/>
                </g>`;
    default: return '';
    }
  };

  const svgString = `
        <svg width="${size}" height="${size}" viewBox="0 0 120 100" xmlns="http://www.w3.org/2000/svg">
            <g ${transform}>
                <!-- Body -->
                <ellipse cx="60" cy="50" rx="50" ry="35" fill="${color}" stroke="rgba(0,0,0,0.2)" stroke-width="2" />
                
                <!-- Markings -->
                ${renderMarkings(markings, color)}
                
                <!-- Ears -->
                <path d="M30,25 Q20,5 40,15" fill="${color}" stroke="rgba(0,0,0,0.2)" />
                <path d="M90,25 Q100,5 80,15" fill="${color}" stroke="rgba(0,0,0,0.2)" />

                <!-- Snout -->
                <ellipse cx="60" cy="65" rx="15" ry="10" fill="rgba(0,0,0,0.1)" />
                <circle cx="55" cy="65" r="2" fill="#333" />
                <circle cx="65" cy="65" r="2" fill="#333" />

                <!-- Eyes -->
                ${expr.eyes}
                
                <!-- Mouth -->
                ${expr.mouth}

                <!-- Tusks -->
                ${tusks}

                <!-- Legs -->
                <rect x="35" y="80" width="10" height="15" fill="${color}" stroke="rgba(0,0,0,0.2)" />
                <rect x="75" y="80" width="10" height="15" fill="${color}" stroke="rgba(0,0,0,0.2)" />
                
                <!-- Accessory -->
                ${renderAccessory(accessory, 0)}
            </g>
        </svg>
    `;

  const result = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  console.log(`🖼️ [proceduralghost] [NPCGenerator] Generated pig SVG (length: ${result.length})`);
  return result;
};
